import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ExternalLink, Search, Loader2, AlertTriangle } from "lucide-react";
import {
  listBallotMeasures,
  resolveBallotMeasure,
  ingestMeasure,
} from "@/lib/api";

const STATUS_KEY_ORDER = ["qualified", "eligible"];

export default function BrowseMeasuresPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [statusUrl, setStatusUrl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const data = await listBallotMeasures();
        const ordered = STATUS_KEY_ORDER
          .map((k) => (data.groups || []).find((g) => g.key === k))
          .filter(Boolean);
        const extras = (data.groups || []).filter(
          (g) => !STATUS_KEY_ORDER.includes(g.key)
        );
        setGroups([...ordered, ...extras]);
        setStatusUrl(data.status_page_url || null);
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.detail || "Could not load ballot measures.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.map((g) => ({
      ...g,
      items: (g.items || []).filter((m) => {
        const hay = [
          m.title,
          m.measure_code,
          m.author,
          m.election,
          m.initiative_number,
          (m.proponents || []).join(" "),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      }),
    }));
  }, [groups, query]);

  const analyze = async (m, groupSourceUrl) => {
    if (busyId) return;
    setBusyId(m.id);
    try {
      const resolved = await resolveBallotMeasure({
        title: m.title,
        source_url: m.official_url,
        measure_code: m.measure_code,
        session: m.session,
        list_source_url: m.list_source_url || groupSourceUrl,
      });

      if (!resolved.can_analyze || !resolved.analysis_source_url) {
        toast.error(
          resolved.warning ||
            "This measure has no machine-readable official source. Paste the full text on the New Scan page."
        );
        return;
      }

      if (resolved.warning) toast.message(resolved.warning);

      const label =
        m.measure_code ||
        (m.initiative_number ? `Initiative ${m.initiative_number}` : m.title);

      const data = await ingestMeasure({
        title: label,
        election_date: m.election || undefined,
        urls: [resolved.analysis_source_url],
        pasted_title: label,
      });
      toast.success(`Analyzing: ${data.measure_id}`);
      navigate(`/measures/${data.id}`);
    } catch (e) {
      console.error(e);
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        "Could not start analysis for that measure.";
      toast.error(detail);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14"
      data-testid="browse-measures-page"
    >
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="stamp stamp-explicit">California · Secretary of State</span>
          <span className="stamp stamp-implied">Official sources only</span>
        </div>
        <h1 className="hero-title text-4xl sm:text-5xl text-[hsl(var(--ink))] mb-4">
          Browse California ballot measures.
        </h1>
        <p className="text-base sm:text-lg text-[hsl(var(--ink-2))] max-w-3xl leading-relaxed">
          Pulled live from the California Secretary of State. Pick a measure and
          Measure X-Ray will fetch the official text and run it through the same
          hostile-contract review used for bills.
        </p>
        {statusUrl && (
          <a
            href={statusUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-mono uppercase tracking-wider text-[hsl(var(--accent-blue))] hover:underline inline-flex items-center gap-1 mt-3"
            data-testid="status-page-link"
          >
            View full qualification status on SOS
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </section>

      <div className="mb-6 flex items-center gap-2 max-w-xl">
        <div className="flex items-center gap-2 flex-1 border border-[hsl(var(--rule))] bg-white rounded-sm px-3 py-2">
          <Search className="w-4 h-4 text-[hsl(var(--ink-muted))]" />
          <input
            data-testid="browse-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, ACA/SB code, or proponent"
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
      </div>

      {loading && (
        <div
          className="text-sm text-[hsl(var(--ink-muted))] flex items-center gap-2"
          data-testid="browse-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Fetching official Secretary of State listings…
        </div>
      )}

      {error && (
        <div
          className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-3 py-2"
          data-testid="browse-error"
        >
          {error}
        </div>
      )}

      {!loading && !error &&
        filteredGroups.map((g) => (
          <section key={g.key} className="mb-12" data-testid={`group-${g.key}`}>
            <div className="flex items-end justify-between mb-3 pb-2 border-b border-dashed border-[hsl(var(--rule))]">
              <div>
                <div className="section-label">{g.label}</div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">
                  {(g.items || []).length} measure
                  {(g.items || []).length === 1 ? "" : "s"}
                </div>
              </div>
              <a
                href={g.source_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-mono uppercase tracking-wider text-[hsl(var(--accent-blue))] hover:underline inline-flex items-center gap-1"
              >
                SOS source
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {g.error && (
              <div
                className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-4 inline-flex items-center gap-2"
                data-testid={`group-${g.key}-error`}
              >
                <AlertTriangle className="w-4 h-4" />
                Could not load this section: {g.error}
              </div>
            )}

            {(!g.items || g.items.length === 0) && !g.error && (
              <div className="text-sm text-[hsl(var(--ink-muted))] italic">
                No measures match.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(g.items || []).map((m) => {
                const isBusy = busyId === m.id;
                const label =
                  m.measure_code ||
                  (m.initiative_number ? `#${m.initiative_number}` : "Measure");
                return (
                  <article
                    key={m.id}
                    className="paper-card rounded-sm p-4 sm:p-5 flex flex-col gap-3"
                    data-testid={`measure-card-${m.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[0.7rem] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                        {label}
                        {m.author ? ` · ${m.author}` : ""}
                      </span>
                      {m.election && (
                        <span className="text-[0.7rem] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                          {m.election}
                        </span>
                      )}
                    </div>

                    <h3 className="font-serif text-lg leading-snug text-[hsl(var(--ink))]">
                      {m.title}
                    </h3>

                    {m.proponents && m.proponents.length > 0 && (
                      <div className="text-xs text-[hsl(var(--ink-muted))]">
                        Proponent{m.proponents.length > 1 ? "s" : ""}:{" "}
                        {m.proponents.join(", ")}
                      </div>
                    )}

                    {m.chapter && (
                      <div className="text-xs text-[hsl(var(--ink-muted))]">
                        {m.chapter}
                      </div>
                    )}

                    <div className="flex items-center gap-3 flex-wrap mt-1">
                      {m.official_url && (
                        <a
                          href={m.official_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-[hsl(var(--accent-blue))] hover:underline inline-flex items-center gap-1"
                          data-testid={`official-link-${m.id}`}
                        >
                          Official PDF
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <div className="ml-auto">
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => analyze(m, g.source_url)}
                          data-testid={`analyze-btn-${m.id}`}
                          className="inline-flex items-center gap-1.5 bg-[hsl(var(--ink))] text-[hsl(var(--paper))] text-sm rounded-sm px-3 py-1.5 disabled:opacity-50 hover:bg-black"
                        >
                          {isBusy ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Starting…
                            </>
                          ) : (
                            <>
                              Analyze measure
                              <ArrowRight className="w-3.5 h-3.5" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
    </div>
  );
}
